// @flow
import React, { Component } from "react";
import classNames from "classnames";
import withStyles from "elevate-ui/withStyles";
import FavoriteListing from "elevate-ui-icons/FavoriteListing";
import SaveSearch from "elevate-ui-icons/SaveSearch";
import VirtualTour from "elevate-ui-icons/VirtualTour";
import Button from "elevate-ui/Button";
import Typography from "elevate-ui/Typography";
import OpenHouses from "./OpenHouses";
import { withGlobalContext } from "../common/GlobalContext";
import { withListingContext } from "./ListingContext";
import ListingCallouts from "../common/ListingCallouts";
import Mortgage from "./Mortgage";
import AiSummary from "./ai-summary";
import type { CompliantListingType } from "flow/types";

type Props = {
  classes: Object,
  className: string,
  compliance: Object,
  favorites: Array<number>,
  listing: CompliantListingType,
  isListingDetail: boolean,
  aiSummary: string | null,
  isAiEnabled: boolean,
  siteUser: Object | null,
  toggleFavoriteListing: (string, ?string, ?string, boolean) => void,
  trackLeadAction: (Object) => void,
};

class Details extends Component<Props> {
  handleClick() {
    // Flow does not recognize `document`
    // $FlowIgnore
    document.getElementById("target").scrollIntoView({
      behavior: "smooth",
    });
  }

  render() {
    const {
      classes,
      className,
      compliance,
      isListingDetail,
      favorites,
      listing,
      aiSummary,
      isAiEnabled,
      toggleFavoriteListing,
      trackLeadAction,
      siteUser,
    } = this.props;

    const isFavorite = favorites && favorites.indexOf(listing.id) > -1;
    return (
      <div className={classNames(classes.root, className)}>
        <div className={classes.mainDetails}>
          <div className={classes.details}>
            {compliance &&
              compliance.officeDisclaimer &&
              compliance.officeDisclaimerAboveAddress && (
                <div className={classes.officeAbove} id="ref__officeDisclaimer">
                  {compliance.officeDisclaimer}
                </div>
              )}
            <div className={classes.typeWrapper}>
              <span className={classes.type}>{listing.type}</span>
              {listing.status === "Active" && (
                <span
                  className={classNames(classes.status, "ref__status-display")}
                >
                  {listing.isRental ? "For Rent" : "For Sale"}
                </span>
              )}
              {listing.status === "Sold" && (
                <span
                  className={classNames(classes.status, "ref__status-display")}
                >
                  {listing.isRental ? "Rented" : "Sold"}
                </span>
              )}
              {listing.status === "Pending" && (
                <span
                  className={classNames(classes.status, "ref__status-display")}
                >
                  Pending
                </span>
              )}
            </div>
            {listing.status === "Sold" &&
            (listing.closePriceFormatted || listing.priceFormatted) ? (
              <div className={classes.price}>
                {listing.priceFormatted && (
                  <div className={classes.listPrice}>
                    <span className={classes.listPriceLabel}>List Price</span>{" "}
                    <span className={classes.listPriceAmount}>
                      {listing.priceFormatted}
                    </span>
                  </div>
                )}
                {listing.closePriceFormatted && (
                  <div>
                    <span className={classes.priceLabel}>Sold Price</span>{" "}
                    {listing.closePriceFormatted}
                  </div>
                )}
              </div>
            ) : (
              <div className={classes.price}>{listing.priceFormatted}</div>
            )}
            <div id="audiostream" />
            <div className={classes.address}>{listing.address}</div>
            <div className={classes.addressBelow}>{listing.cityStateZip}</div>
            {compliance &&
              compliance.officeDisclaimer &&
              !compliance.officeDisclaimerAboveAddress && (
                <div className={classes.office} id="ref__officeDisclaimer">
                  {compliance.officeDisclaimer}
                </div>
              )}
            {compliance &&
              compliance.listingResults &&
              compliance.listingResults.showCooperatingBrokerage &&
              listing.status === "Sold" &&
              listing.soldCooperativeOfficeName && (
                <div
                  className={classes.office}
                  id="ref__soldCooperativeOfficeName"
                >
                  Cooperating Brokerage: {listing.soldCooperativeOfficeName}
                </div>
              )}
            {compliance &&
              compliance.mlsDisclaimer && (
                <div className={classes.mls} id="ref__mlsDisclaimer">
                  {compliance.mlsDisclaimer}
                </div>
              )}
          </div>
          <div className={classes.actions} id="ref__listing-actions">
            <button
              onClick={(e) => {
                e.preventDefault();
                toggleFavoriteListing(
                  listing.id,
                  listing.mlsListingId,
                  listing.zipCode,
                  listing.compliance && listing.compliance.usesListTrac
                );
              }}
              className={classes.action}
              style={{ marginRight: "10px" }}
            >
              <FavoriteListing
                size={30}
                style={{
                  color: isFavorite ? "#ff7070" : "#60686f",
                }}
              />
              <span style={{ marginTop: "6px" }}>
                {isFavorite ? "Favorited" : "Favorite"}
              </span>
            </button>
            {listing.virtualTour && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  trackLeadAction({
                    action: "viewed-virtual-tour",
                    listingId: `${listing.id}`, // API expects listingId as a string
                    virtualTourUrl: listing.virtualTour,
                  });
                  window.open(listing.virtualTour, "_blank");
                }}
                className={classes.action}
                style={{ marginRight: "10px", lineHeight: "1.2" }}
              >
                <VirtualTour size={32} color="#60686f" />
                <span style={{ marginTop: "6px" }}>Virtual Tour</span>
              </button>
            )}
            <form
              method="POST"
              action={`/saved-searches?city=${
                listing.city ? listing.city : ""
              }&state=${listing.state ? listing.state : ""}&minPrice=${
                listing.price ? Math.floor(parseFloat(listing.price) * 0.8) : 0
              }&maxPrice=${
                listing.price ? Math.ceil(parseFloat(listing.price) * 1.2) : 0
              }&type=${listing.type ? listing.type : ""}`}
              onSubmit={() => {
                trackLeadAction({
                  action: "saved-search-similar-homes",
                  listingId: `${listing.id}`, // API expects listingId as a string
                });
              }}
            >
              <input
                type="hidden"
                name="name"
                value={`Similar to ${listing.address ? listing.address : ""}`}
              />
              <input type="hidden" name="back" value="true" />
              <button
                type="submit"
                className={classNames(classes.action, classes.saveSearch)}
              >
                <SaveSearch size={36} color="#60686f" />
                <span>Save search</span>
              </button>
            </form>
          </div>
        </div>
        <div className={classes.mainDetails}>
          <OpenHouses />
        </div>
        {isListingDetail && (
          <div className={classes.contactButtonContainer}>
            <Button
              onClick={this.handleClick}
              className={classes.contactButton}
            >
              Contact agent
            </Button>
          </div>
        )}
        {isAiEnabled && (
          <AiSummary
            classes={classes}
            listing={listing}
            aiSummary={aiSummary}
            siteUser={siteUser}
          />
        )}
        <div className={classes.callOutMortgage}>
          <ListingCallouts listing={listing} />
          {!listing.isRental ? (
            <Mortgage
              address={listing.address}
              price={listing.price}
              datastoreProviderId={listing.datastoreProviderId}
            />
          ) : null}
        </div>

        <Typography
          type="body"
          className={classes.publicRemarks}
          dangerouslySetInnerHTML={{
            __html: listing.publicRemarks,
          }}
        />
      </div>
    );
  }
}

export default withStyles((theme) => ({
  root: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  mainDetails: {
    display: "flex",
    flexDirection: "column",
    marginBottom: theme.spacing.unit,

    [theme.breakpoints(1000)]: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginBottom: "0px",
    },
  },
  details: {
    display: "flex",
    flexDirection: "column",
    paddingBottom: theme.spacing.unit,
  },
  action: {
    fontSize: "14px",
    color: theme.colors.gray["900"],
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "78px",
    border: `2px solid ${theme.colors.gray["200"]}`,
    borderRadius: theme.globalBorderRadius,

    [theme.breakpoints(1000)]: {
      width: "100px",
    },
  },
  actions: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",

    [theme.breakpoints(1000)]: {
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "flex-start",
    },
  },
  saveSearch: {
    width: "100%",

    [theme.breakpoints(1000)]: {
      width: "100px",
    },
  },
  contactButtonContainer: {
    marginBottom: theme.spacing.unit,

    [theme.breakpoints(600)]: {
      display: "none",
    },
  },
  contactButton: {
    width: "100%",
  },
  publicRemarks: {
    display: "flex",
    flexDirection: "column",
    maxWidth: "660px",
    marginTop: theme.spacing.unit,
  },
  typeWrapper: {
    display: "flex",
    alignItems: "center",
  },
  type: {
    fontSize: "18px",
    fontWeight: "600",
    color: theme.colors.gray["800"],
    lineHeight: "1",
    textTransform: "uppercase",
  },
  status: {
    color: theme.colors.gray["800"],
    fontSize: "12px",
    fontWeight: "600",
    lineHeight: "1",
    border: `1px solid ${theme.colors.gray["800"]}`,
    borderRadius: theme.globalBorderRadius,
    padding: "2px 4px",
    marginLeft: theme.spacing.unit * 0.75,
  },
  price: {
    color: theme.colors.gray["900"],
    fontSize: "40px",
    fontWeight: "600",
    marginTop: theme.spacing.unit * 0.75,
    marginBottom: theme.spacing.unit,
  },
  priceLabel: {
    fontSize: "20px",
  },
  listPrice: {
    marginBottom: theme.spacing.unit * 0.75,
    color: theme.colors.gray["500"],
  },
  listPriceLabel: {
    fontSize: "20px",
  },
  listPriceAmount: {
    marginLeft: theme.spacing.unit * 0.5,
  },
  address: {
    color: theme.colors.gray["800"],
    fontSize: "20px",
    lineHeight: "1.4",
    fontWeight: "400",
  },
  addressBelow: {
    color: theme.colors.gray["800"],
    fontSize: "16px",
    lineHeight: "1.4",
    fontWeight: "400",
  },
  office: {
    color: theme.colors.gray["900"],
    fontSize: "16px",
    lineHeight: "1.4",
    fontWeight: "400",
    marginTop: theme.spacing.unit * 0.75,
    whiteSpace: "pre-line",
  },
  officeAbove: {
    color: theme.colors.gray["900"],
    fontSize: "20px",
    lineHeight: "1.4",
    fontWeight: "400",
    marginBottom: theme.spacing.unit * 0.75,
    whiteSpace: "pre-line",
  },
  mls: {
    color: theme.colors.gray["900"],
    fontSize: "12px",
    lineHeight: "1.4",
    fontWeight: "400",
    marginTop: theme.spacing.unit * 0.75,
  },
  additional: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    width: "100%",
    borderTop: `1px solid ${theme.colors.gray["200"]}`,
    borderBottom: `1px solid ${theme.colors.gray["200"]}`,
    padding: "8px 0",
  },
  detail: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing.unit * 0.5,
  },
  aiSummary: {
    padding: "16px",
    borderRadius: "2px",
    border: `1px solid ${theme.colors.gray["200"]}`,
  },
  aiSummaryHeader: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    alignSelf: "stretch",
  },
  aiHeader: {
    borderRadius: "2px",
    border: `1px solid ${theme.colors.primary["500"]}`,
    background: `linear-gradient(180deg, ${theme.colors.primary["500"]} 0%, ${
      theme.colors.primary["500"]
    } 100%)`,
    padding: "1px 8px",
    fontSize: 12,
    lineHeight: "20px",
  },
  callOutMortgage: {
    display: "flex",
    flexDirection: "column",
    borderTop: `1px solid ${theme.colors.gray["200"]}`,
    borderBottom: `1px solid ${theme.colors.gray["200"]}`,

    [theme.breakpoints(1000)]: {
      flexDirection: "row",
    },
  },
}))(withGlobalContext(withListingContext(Details)));
